import { ActionIcon, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconHistory, IconPlus } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import HistoryModal from "src/components/shared/HistoryModal/HistoryModal";
import LocalSearch from "src/components/shared/LocalSearch/LocalSearch";
import { useGetApi } from "src/hooks";
import { ButtonsWrapper, PageContainer } from "src/styled";
import { getYachtId } from "src/utils";

import {
  matchesTankPlanSearch,
  type actionType,
  type ITankPlan,
} from "../../../../components/pages/Settings/YachtSetUp/TankPlans/tankPlans.model";
import TankPlansAddModal from "../../../../components/pages/Settings/YachtSetUp/TankPlans/TankPlansAddModal";
import TankPlansTable from "../../../../components/pages/Settings/YachtSetUp/TankPlans/TankPlansTable/TankPlansTable";

const TankPlans = () => {
  const yachtid = getYachtId();
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] =
    useDisclosure(false);

  const [
    historyModalOpened,
    { open: openHistoryModal, close: closeHistoryModal },
  ] = useDisclosure(false);

  const [selectedRow, setSelectedRow] = useState<ITankPlan | undefined>();
  const [tableSortedData, setTableSortedData] = useState<ITankPlan[]>([]);
  const [displayTanks, setDisplayTanks] = useState<ITankPlan[]>([]);
  const [resetSearch, setResetSearch] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const isSearchingRef = useRef(false);
  const { data, refetch } = useGetApi<{ list: ITankPlan[] }>({
    request: "get-tanks",
    // mapper: data => data?.list ?? [],
  });
  const handleAction = (action: actionType, rowIdx: number) => {
    if (!tableSortedData.length) return;
    setSelectedRow(tableSortedData[rowIdx]);
    if (action === "history") openHistoryModal();
    else openAddModal();
  };

  const handleClose = (action: actionType) => {
    setSelectedRow(undefined);
    if (action === "history") closeHistoryModal();
    else closeAddModal();
  };

  const sortedData = useMemo(
    () =>
      data?.list ? [...data.list].sort((a, b) => a.order - b.order) : [],
    [data],
  );

  useEffect(() => {
    if (!isSearchingRef.current) {
      setDisplayTanks(sortedData);
    }
  }, [sortedData]);

  const handleSetDisplayTanks = useCallback(
    (tanks: ITankPlan[]) => {
      isSearchingRef.current =
        tanks.length !== sortedData.length && sortedData.length > 0;
      setDisplayTanks(tanks);
    },
    [sortedData],
  );

  const handleRefetch = useCallback(async () => {
    await refetch();
    isSearchingRef.current = false;
    setSearchQuery("");
    setResetSearch(Math.random());
  }, [refetch]);

  const isSearchActive = searchQuery.trim().length > 0;

  return (
    <PageContainer>
      <Breadcrumbs border={false}>
        <ButtonsWrapper style={{ gap: 8, marginLeft: 12 }}>
          {sortedData.length > 0 && (
            <LocalSearch
              defaultData={sortedData}
              setData={handleSetDisplayTanks}
              filterFn={matchesTankPlanSearch}
              resetSearch={resetSearch}
              setResetSearch={setResetSearch}
              onSearchChange={setSearchQuery}
            />
          )}
          <Tooltip withArrow label="History">
            <ActionIcon
              size={36}
              variant="outline"
              color="blue"
              onClick={openHistoryModal}>
              <IconHistory size={22} />
            </ActionIcon>
          </Tooltip>
          <Tooltip withArrow label="Add">
            <ActionIcon
              size={36}
              variant="outline"
              color="blue"
              onClick={openAddModal}>
              <IconPlus size={22} />
            </ActionIcon>
          </Tooltip>
        </ButtonsWrapper>
      </Breadcrumbs>
      {sortedData.length > 0 && (
        <TankPlansTable
          onAction={handleAction}
          data={displayTanks}
          refetch={handleRefetch}
          onSortedDataChange={setTableSortedData}
          syncOrder={!isSearchActive}
        />
      )}
      <TankPlansAddModal
        opened={addModalOpened}
        close={() => handleClose("add")}
        refetch={handleRefetch}
        values={selectedRow}
      />

      <HistoryModal
        opened={historyModalOpened}
        close={() => handleClose("history")}
        elementName={selectedRow?.name}
        dataHeaders={["Tank Name"]}
        dataKeys={["name"]}
        size="55vw"
        params={{
          ...(selectedRow?.tankid
            ? { tankid: selectedRow?.tankid }
            : { yachtid, full: true }),
        }}
        apiUrl="get-tanks-history"
      />
    </PageContainer>
  );
};

export default TankPlans;
